import { SuccessFactorsProvider } from './successfactors/SuccessFactorsProvider';
import { DropboxProvider } from './dropbox/DropboxProvider';
import { WrikeProvider } from './wrike/WrikeProvider';
import { TrelloProvider } from './trello/TrelloProvider';
import { ShortcutProvider } from './shortcut/ShortcutProvider';
import { HubSpotProvider } from './hubspot/HubSpotProvider';
import { ZoomProvider } from './zoom/ZoomProvider';
import { SnowflakeProvider } from './snowflake/SnowflakeProvider';
import { AdpProvider } from './adp/AdpProvider';
import { CheckrProvider } from './checkr/CheckrProvider';
import { ClickUpProvider } from './clickup/ClickUpProvider';
import { AsanaProvider } from './asana/AsanaProvider';
import { MondayProvider } from './monday/MondayProvider';
import { BoxProvider } from './box/BoxProvider';
import { SalesforceProvider } from './salesforce/SalesforceProvider';
import { ServiceNowProvider } from './servicenow/ServiceNowProvider';
import { AikidoProvider } from './aikido/AikidoProvider';
import { IntegrationProvider } from './types';
import { GitHubProvider } from './github/GitHubProvider';
import { BitbucketProvider } from './bitbucket/BitbucketProvider';
import { GitLabProvider } from './gitlab/GitLabProvider';
import { JiraProvider } from './jira/JiraProvider';
import { LinearProvider } from './linear/LinearProvider';
import { IntercomProvider } from './intercom/IntercomProvider';
import { FreshdeskProvider } from './freshdesk/FreshdeskProvider';
import { DocusaurusProvider } from './external-docs/ExternalDocsProvider';
import { VitePressProvider } from './external-docs/ExternalDocsProvider';
import { GitBookProvider } from './external-docs/ExternalDocsProvider';
import { LocalFileProvider } from './local-file/LocalFileProvider';
import { IntegrationType } from '@deepguide-ai/types';

import { GoogleDriveProvider } from './google-drive/GoogleDriveProvider';
import { ConfluenceProvider } from './confluence/ConfluenceProvider';
import { NotionProvider } from './notion/NotionProvider';
import { ZendeskProvider } from './zendesk/ZendeskProvider';
import { MicrosoftTeamsProvider } from './microsoft-teams/TeamsProvider';
import { SlackProvider } from './slack/SlackProvider';
import { VantaProvider } from './vanta/VantaProvider';
import { DrataProvider } from './drata/DrataProvider';
import {
  AwsReadonlyProvider,
  AzureReadonlyProvider,
  CloudflareProvider,
  TencentProvider,
  AliyunProvider,
  GcpReadonlyProvider,
} from './cloud/CloudProviders';
import {
  IntuneProvider,
  FleetDMProvider,
  JamfProProvider,
} from './mdm/MdmProviders';
import { OpenAttestProvider } from './open-attest/OpenAttestProvider';
import {
  OktaProvider,
  GoogleWorkspaceProvider,
  Auth0Provider,
  OneLoginProvider,
  JumpCloudProvider,
  DatadogProvider,
  AxiomProvider,
  SentryProvider,
  PagerDutyProvider,
  IncidentIoProvider,
  KandjiProvider,
  KnowBe4Provider,
  VercelProvider,
  RenderProvider,
  CrowdStrikeProvider,
  SnykProvider,
  SupabaseProvider,
  WizProvider,
  OrcaProvider,
  BambooHRProvider,
  RipplingProvider,
  DeelProvider,
  WorkdayProvider,
  StripeProvider,
  BrexProvider,
  KubernetesProvider,
} from './coming-soon/ComingSoonProvider';
import { M365Provider } from './m365/M365Provider';
import { GustoProvider } from './gusto/GustoProvider';
import { HibobProvider } from './hibob/HibobProvider';
import { QuickBooksProvider } from './quickbooks/QuickBooksProvider';
import { AzureDevOpsProvider } from './azure-devops/AzureDevOpsProvider';

// Use Partial<Record> to allow missing implementations
const providers: Partial<Record<IntegrationType, IntegrationProvider>> = {
  GitHub: GitHubProvider,
  Bitbucket: BitbucketProvider,
  GitLab: GitLabProvider,
  Jira: JiraProvider,
  Linear: LinearProvider,
  Asana: AsanaProvider,
  ServiceNow: ServiceNowProvider,
  Box: BoxProvider,
  Snowflake: SnowflakeProvider,
  Monday: MondayProvider,
  ClickUp: ClickUpProvider,
  Intercom: IntercomProvider,
  Freshdesk: FreshdeskProvider,
  Docusaurus: DocusaurusProvider,
  VitePress: VitePressProvider,
  GitBook: GitBookProvider,
  LocalFile: LocalFileProvider,
  MicrosoftTeams: MicrosoftTeamsProvider,
  Slack: SlackProvider,
  GoogleDrive: GoogleDriveProvider,
  Confluence: ConfluenceProvider,
  Notion: NotionProvider,
  Zendesk: ZendeskProvider,
  Vanta: VantaProvider, // RFC-085: Compliance automation
  Drata: DrataProvider, // RFC-085: Compliance automation
  AWS_READONLY: AwsReadonlyProvider,
  GCP_READONLY: GcpReadonlyProvider,
  AZURE_READONLY: AzureReadonlyProvider,
  AZURE_DEVOPS: AzureDevOpsProvider,
  Cloudflare: CloudflareProvider,
  TencentCloud: TencentProvider,
  Aliyun: AliyunProvider,
  Supabase: SupabaseProvider,
  MicrosoftIntune: IntuneProvider,
  FleetDM: FleetDMProvider,
  JamfPro: JamfProProvider,
  // Identity Providers (RFC-148)
  Okta: OktaProvider,
  GoogleWorkspace: GoogleWorkspaceProvider,
  Auth0: Auth0Provider,
  OneLogin: OneLoginProvider,
  JumpCloud: JumpCloudProvider,
  // Security & Monitoring (RFC-148)
  Datadog: DatadogProvider,
  Axiom: AxiomProvider,
  Sentry: SentryProvider,
  PagerDuty: PagerDutyProvider,
  IncidentIo: IncidentIoProvider,
  Kandji: KandjiProvider,
  KnowBe4: KnowBe4Provider,
  Vercel: VercelProvider,
  Render: RenderProvider,
  CrowdStrike: CrowdStrikeProvider,
  Snyk: SnykProvider,
  Aikido: AikidoProvider,
  Wiz: WizProvider,
  Orca: OrcaProvider,
  // HR & People Ops (RFC-148)
  BambooHR: BambooHRProvider,
  Rippling: RipplingProvider,
  Deel: DeelProvider,
  Gusto: GustoProvider,
  HiBob: HibobProvider,
  ADP: AdpProvider,
  SuccessFactors: SuccessFactorsProvider,
  Workday: WorkdayProvider,
  Checkr: CheckrProvider,
  // Finance & Billing (RFC-148)
  Stripe: StripeProvider,
  QuickBooks: QuickBooksProvider,
  Brex: BrexProvider,
  // CRM & Sales (RFC-148)
  Salesforce: SalesforceProvider,
  Shortcut: ShortcutProvider,
  Dropbox: DropboxProvider,
  Trello: TrelloProvider,
  Wrike: WrikeProvider,
  HubSpot: HubSpotProvider,
  // Collaboration (RFC-148)
  Zoom: ZoomProvider,
  // Cloud & Infrastructure (RFC-148)
  M365: M365Provider,
  Kubernetes: KubernetesProvider,
  OpenAttest: OpenAttestProvider,
  Other: LocalFileProvider, // Fallback
}; 

/**
 * Case-insensitive fallback index.
 *
 * `IntegrationType` values are mixed-case (`Supabase`, `M365`, `AWS_READONLY`)
 * and the OAuth callback route resolves a provider from a URL path segment
 * (`/auth-callback/:provider`). Any hop that normalizes case — an OAuth
 * provider echoing back a lowercased redirect path, a proxy, a hand-typed
 * URL — used to land on `undefined` and render "Unsupported provider", with
 * the real cause invisible.
 */
const providersByLowerKey = new Map(
  Object.entries(providers).map(([key, provider]) => [key.toLowerCase(), provider]),
);

export const getIntegrationProvider = (type: IntegrationType): IntegrationProvider | undefined => {
  return providers[type] ?? providersByLowerKey.get(String(type).toLowerCase());
};

// Export all integration providers
export { providers };
